import type { ReactNode } from 'react';

export interface NavItem {
  /** Catalog key for the visible label (see src/i18n/catalog.ts, `nav.item.*`). */
  labelKey: string;
  path: string;
  /** End-match (only highlight on exact path). */
  end?: boolean;
  /** Workstream tag rendered as a right-side badge ("W15", "soon", "demo"). */
  workstream?: string;
  /** Icon node: passed to AppShell.NavLink's `leftSection`. */
  icon?: ReactNode;
  /** Optional supporting description (shown only in `full` navbar mode), as a catalog key. */
  descriptionKey?: string;
}

export interface NavSection {
  /** Catalog key for the section overline (`nav.section.*`). */
  labelKey: string;
  /** Section items. */
  items: NavItem[];
}

/**
 * Single source of truth for the admin sidebar: grouped by responsibility,
 * mirrors the redesign brief.
 *
 * Icons stay symbol-free intentionally; the Sidebar component injects lucide
 * icons (or sdk-ui builtin icons) by `path` lookup. This keeps the nav data
 * pure JSON-shaped for easier future export to a backend-driven menu.
 */
export const ADMIN_NAV: NavSection[] = [
  {
    // The dashboard stands alone above the grouped sections: it is where every
    // session starts, not one entry among peers.
    labelKey: 'nav.section.overview',
    items: [
      { labelKey: 'nav.item.dashboard', path: '/', end: true }
    ]
  },
  {
    labelKey: 'nav.section.tenantManagement',
    items: [
      { labelKey: 'nav.item.tenants', path: '/tenants' },
      { labelKey: 'nav.item.users', path: '/users' },
      { labelKey: 'nav.item.roles', path: '/roles' }
    ]
  },
  {
    labelKey: 'nav.section.billing',
    items: [
      { labelKey: 'nav.item.billing', path: '/billing', end: true },
      { labelKey: 'nav.item.billingLedger', path: '/billing/ledger' },
      { labelKey: 'nav.item.billingInvoices', path: '/billing/invoices' },
      { labelKey: 'nav.item.billingPlans', path: '/billing/plans' },
      { labelKey: 'nav.item.billingSkuRates', path: '/billing/sku-rates' },
      // AI models (14 October 2026): what the AI API sells, beside the rates that price it.
      { labelKey: 'nav.item.billingAiModels', path: '/billing/ai-models' },
      // Languages (1 November 2026): what chatbots may speak, beside the AI models that speak them.
      { labelKey: 'nav.item.billingLanguages', path: '/billing/languages' },
      // Meta news feed (15 September 2026): what Meta changes, beside the rates it affects.
      { labelKey: 'nav.item.billingMetaUpdates', path: '/billing/meta-updates' },
      { labelKey: 'nav.item.billingSubscriptions', path: '/billing/subscriptions' },
      { labelKey: 'nav.item.billingUsage', path: '/billing/usage' },
      { labelKey: 'nav.item.billingWalletAlerts', path: '/billing/wallet-alerts' },
      { labelKey: 'nav.item.billingItn', path: '/billing/itn' }
    ]
  },
  {
    labelKey: 'nav.section.partners',
    items: [
      { labelKey: 'nav.item.partners', path: '/partners', end: true },
      { labelKey: 'nav.item.partnerWithdrawals', path: '/partners/withdrawals' },
      { labelKey: 'nav.item.partnerSettings', path: '/partners/settings' }
    ]
  },
  {
    labelKey: 'nav.section.messaging',
    items: [
      { labelKey: 'nav.item.conversations', path: '/conversations' },
      { labelKey: 'nav.item.broadcasts', path: '/broadcasts' },
      { labelKey: 'nav.item.templates', path: '/templates' },
      { labelKey: 'nav.item.flows', path: '/flows' },
      { labelKey: 'nav.item.assistantTranscripts', path: '/flows/assistant' },
      { labelKey: 'nav.item.media', path: '/media' },
      { labelKey: 'nav.item.webhooks', path: '/webhooks' },
      { labelKey: 'nav.item.gateways', path: '/gateways' },
      { labelKey: 'nav.item.gatewaysNumbers', path: '/gateways/numbers' },
      { labelKey: 'nav.item.metaStatus', path: '/gateways/meta-status' },
      { labelKey: 'nav.item.voice', path: '/voice' },
      { labelKey: 'nav.item.ussd', path: '/ussd' }
    ]
  },
  {
    labelKey: 'nav.section.content',
    items: [
      { labelKey: 'nav.item.contentNews', path: '/content/news' },
      { labelKey: 'nav.item.contentCompany', path: '/content/company' },
      { labelKey: 'nav.item.contentAgreements', path: '/content/agreements' }
    ]
  },
  {
    labelKey: 'nav.section.platform',
    items: [
      { labelKey: 'nav.item.settings', path: '/settings', end: true }
    ]
  },
  {
    labelKey: 'nav.section.operations',
    items: [
      { labelKey: 'nav.item.system', path: '/system', end: true },
      { labelKey: 'nav.item.systemHealth', path: '/system/health' },
      { labelKey: 'nav.item.logs', path: '/logs' },
      { labelKey: 'nav.item.audit', path: '/audit' },
      { labelKey: 'nav.item.apimonitoring', path: '/api-monitoring' },
      { labelKey: 'nav.item.queuemonitoring', path: '/queue-monitoring' }
    ]
  }
];

/**
 * The one menu item the page belongs to (4 October 2026): the longest item path the address is at or under.
 * Each row used to match itself alone, so on /flows/assistant both Flows and Build with AI lit up, and a page
 * under an item marked `end` (/settings/integrations under Settings) lit nothing. A path matches only at a
 * segment boundary; `/` matches only itself.
 */
export function activeNavPath(pathname: string, paths: readonly string[] = ADMIN_NAV.flatMap((s) => s.items.map((i) => i.path))): string | null {
  let best: string | null = null;
  for (const p of paths) {
    const hit = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p.endsWith('/') ? p : p + '/');
    if (hit && (best === null || p.length > best.length)) best = p;
  }
  return best;
}
